import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { fetchProducts } from "../api";
import { useSEO } from "../hooks/useSEO";
import { ProductCard } from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { CAMPAIGN_FILM_URL, IMG, MENU_GROUPS, HOME_TEE_SLUGS } from "../config/brand";

export default function Home() {
  const [tees, setTees] = useState([]);
  const editorialRef = useRef(null);
  const heroRef = useRef(null);

  useSEO(
    "RICH KIDS FUNERAL — FIRST COLLECTION 26",
    "RICH KIDS FUNERAL. First Collection 26. Campaign, editorial and film."
  );

  useEffect(() => {
    fetchProducts({ category: "tees" })
      .then((all) =>
        setTees(HOME_TEE_SLUGS.map((s) => all.find((p) => p.slug === s)).filter(Boolean))
      )
      .catch(() => {});
  }, []);

  const { scrollYProgress: edProgress } = useScroll({
    target: editorialRef,
    offset: ["start end", "end start"],
  });
  const edY = useTransform(edProgress, [0, 1], ["-6%", "6%"]);

  const categories = MENU_GROUPS.flatMap((g) => g.links);

  return (
    <div data-testid="home-page">
      {/* HERO — full-bleed campaign film */}
      <section ref={heroRef} className="relative w-full bg-black" data-testid="hero-section">
        <motion.div
          className="relative w-full overflow-hidden bg-black"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* The source film has black pillarbox bars baked in (content = centre 75% of width).
              Scaling by 4/3 crops them so the picture runs edge to edge. */}
          <video
            src={CAMPAIGN_FILM_URL}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-[70vh] md:h-[100vh] object-cover block scale-[1.3334] origin-center"
            data-testid="hero-video"
          />
        </motion.div>
      </section>

      {/* MODEL PHOTOGRAPHY — full-bleed, no gutters. Mobile gets Paly-style thin captions. */}
      <section className="w-full" data-testid="model-section">
        <div className="grid grid-cols-1 md:grid-cols-2">
          <Link to="/products/different-personalities-tee" data-testid="model-link-dp-tee" className="block group">
            <p className="md:hidden px-2 py-2 text-[12px] tracking-[0.02em] uppercase bg-white">Different Personalities Tee</p>
            <div className="overflow-hidden bg-black aspect-[1/1]">
              <img
                src={IMG.modelDP}
                alt="RKF Different Personalities Tee campaign portrait"
                loading="lazy"
                className="w-full h-full object-cover block transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              />
            </div>
          </Link>
          <Link to="/products/angel-f-tee" data-testid="model-link-angel-tee" className="block group">
            <p className="md:hidden px-2 py-2 text-[12px] tracking-[0.02em] uppercase bg-white">Angel F Tee</p>
            <div className="overflow-hidden bg-black aspect-[1/1]">
              <img
                src={IMG.modelAngel}
                alt="RKF Angel F Tee campaign portrait"
                loading="lazy"
                className="w-full h-full object-cover block transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              />
            </div>
          </Link>
        </div>
      </section>

      {/* EDITORIAL CAMPAIGN — full-bleed */}
      <section className="w-full" data-testid="editorial-section">
        <Link
          to="/editorial"
          data-testid="editorial-link"
          ref={editorialRef}
          className="block group"
        >
          <div className="overflow-hidden aspect-[3/2] bg-black">
            <motion.img
              src={IMG.editorial}
              alt="RKF editorial — Different Personalities Tee campaign portrait"
              loading="lazy"
              style={{ y: edY }}
              className="w-full h-[112%] -mt-[6%] object-cover block transition-transform duration-700 ease-out group-hover:scale-[1.02]"
            />
          </div>
        </Link>
      </section>

      {/* FALL/WINTER COLLECTION 26 */}
      <section className="px-3 md:px-6 pt-16 md:pt-24" data-testid="collection-section" id="collection">
        <Reveal>
          <Link to="/collections/fall-winter-26" data-testid="fw26-link" className="block group">
            <h1
              className="font-anton uppercase text-[#e50000] leading-[0.88] tracking-[-0.01em] text-[17vw] md:text-[11vw] group-hover:opacity-80 transition-opacity"
              data-testid="fw26-heading"
            >
              Fall/Winter
              <br />
              Collection <span className="text-black">26</span>
            </h1>
          </Link>
        </Reveal>

        <Reveal delay={0.05}>
          <div
            className="flex flex-wrap gap-x-1 gap-y-2 mt-8 md:mt-12"
            data-testid="collection-categories"
          >
            {categories.map((c, i) => (
              <span key={c.to} className="flex items-center">
                <Link
                  to={c.to}
                  data-testid={`collection-cat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[11px] font-medium tracking-[0.18em] px-2 py-1 hover:bg-black hover:text-white transition-colors"
                >
                  {c.label}
                </Link>
                {i < categories.length - 1 && (
                  <span className="text-black/30 text-[11px]">/</span>
                )}
              </span>
            ))}
          </div>
        </Reveal>

        {/* TEES — blue + white tee, Paly-sized tiles. Heading links to full tees page. */}
        <Reveal delay={0.1}>
          <div className="mt-14 md:mt-20" data-testid="featured-products">
            <Link
              to="/tees"
              data-testid="home-tees-heading-link"
              className="inline-flex items-center gap-3 text-[11px] font-medium tracking-[0.24em] border-b border-black pb-1 hover:text-[#e50000] hover:border-[#e50000] transition-colors"
            >
              TEES <span aria-hidden="true">→</span>
            </Link>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 md:gap-x-6 gap-y-10 mt-6 md:mt-8">
              {tees.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-12 text-center">
          <Link
            to="/all-products"
            data-testid="view-all-link"
            className="inline-block text-[11px] font-medium tracking-[0.2em] border border-black px-10 py-4 hover:bg-black hover:text-white transition-colors"
          >
            VIEW ALL PRODUCTS
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
